import { TabListRequestParams, getTabList } from "@/api/tab";
import { usePageState, useTabbarData } from "@/context/PageStateContext";
import { getCommonParams } from "@/lib/commonParams";
import { C_TYPE_ENUM } from "@/lib/constant";
import { Level, reportCustomMetric, reportError, reportPerfMetric } from "@/lib/owl";
import { getYuanFromFen } from "@/lib/price";
import { CouponData } from "@/types/coupon-wall";
import { CouponSelection, QuickSelection, Tab } from "@/types/tab";
import { useCallback, useEffect, useRef, useState } from "react";

// 外卖普通快筛缓存
interface QuickSelectionListCacheItem {
  quickSelectionList: QuickSelection[];
  timestamp: number;
}

// 获取快筛缓存结果
type GetCacheQuickSelectionListResult = {
  isCacheValid: boolean;
  quickSelectionList: QuickSelection[];
};

// 无快筛缓存时的默认值
const NO_QUICK_SELECTION_LIST_CACHE: GetCacheQuickSelectionListResult = {
  isCacheValid: false,
  quickSelectionList: [],
};

export const reportGetTabListSuccessRate = (tabList: Tab[]) => {
  const hasValidData = Array.isArray(tabList) && tabList.length > 0;
  reportCustomMetric({
    key: "getTabList",
    value: hasValidData ? 1 : 0,
  })
}

/**
 * 将优惠券数据格式化为优惠券选择项
 * @param coupon 优惠券数据
 * @returns 格式化后的优惠券选择项
 */
export const formatCouponSelection = (coupon: CouponData): CouponSelection => {
  const { couponSupply, priceLimit, couponViewId, couponValue } = coupon;
  const priceLimitYuan = getYuanFromFen(priceLimit);
  const couponValueYuan = getYuanFromFen(couponValue);
  // 处理无门槛券的显示文本
  const selectionName = priceLimit ? `${priceLimitYuan}-${couponValueYuan}券` : `${couponValueYuan}元无门槛券`;
  
  return {
    name: selectionName,
    id: couponViewId!,
    value: couponSupply!.wayneActId,
    iconUrl: null,
    coupon: {
      ...coupon,
      couponValue: couponValueYuan,
      priceLimit: priceLimitYuan,
    },
  };
};

/**
 * 导航栏 + 快筛栏相关的状态&方法
 * @returns
 */
export default function useTabbar(props: {
  searchRuleScene: number;
  defaultBizScene: number; // 第一个 Tab 的 listScene，用于判断是否选中了第一个 Tab，第一个 Tab 会有券快筛
  enableQuickSelectionLazyLoad?: boolean; // 是否开启普通快筛的懒加载，默认不开启；开启时默认接口不会返回普通快筛，在用户切换 Tab 时需要请求快筛数据
}) {
  const { searchRuleScene, defaultBizScene, enableQuickSelectionLazyLoad = false } = props || {};
  const {
    locationInfo,
    quickScreenCouponList, // 默认展示券快筛列表
    setCouponRecallInfo, // 用户点击去使用出现的券快筛被取消选中时，重置全局状态
    setModuleLoaded, // 标记模块加载状态
  } = usePageState();

  // 获取SSR预取的Tabbar数据
  const { tabbarData } = useTabbarData();
  
  // 是否初始化完成，未初始化完成展示骨架屏
  // 如果SSR预取了数据，直接标记为已初始化
  const [isInitFinished, setIsInitFinished] = useState<boolean>(() => {
    return !!tabbarData;
  });
  // Tab 栏数据
  // 从SSR预取数据初始化
  const [tabList, setTabList] = useState<Tab[]>(() => {
    return (tabbarData?.tabList as Tab[]) || [];
  });
  // 当前选中的 Tab
  // 从SSR预取数据初始化（默认选中第一个）
  const [activeTab, setActiveTab] = useState<Tab | null>(() => {
    const list = (tabbarData?.tabList as Tab[]) || [];
    return list[0] || null;
  });
  // 用户点击红包墙去使用的券，对应的券快筛数据
  const [useCouponSelection, setUseCouponSelection] = useState<CouponSelection | null>(null);
  // 普通快筛列表
  // SSR优化：从tabbarData的第一个Tab的quickSelection初始化
  const [quickSelectionList, setQuickSelectionList] = useState<QuickSelection[]>(() => {
    const list = (tabbarData?.tabList as Tab[]) || [];
    const firstTab = list[0];
    return firstTab?.quickSelection || [];
  });
  // 券快筛列表
  // SSR优化：从quickScreenCouponList初始化券快筛（取前两个）
  const [couponSelectionList, setCouponSelectionList] = useState<CouponSelection[]>(() => {
    if (!Array.isArray(quickScreenCouponList)) return [];
    return quickScreenCouponList
      .filter((item) => item?.couponSupply?.wayneActId)
      .map(formatCouponSelection)
      .slice(0, 2);
  });
  // 当前选中的普通快筛
  const [activeQuickSelection, setActiveQuickSelection] = useState<QuickSelection | null>(null);
  // 当前选中的券快筛
  const [activeCouponSelection, setActiveCouponSelection] = useState<CouponSelection | null>(null);
  // 券快筛对应的普通快筛缓存
  const cacheQuickSelectionListRef = useRef<Map<number, QuickSelectionListCacheItem>>(new Map());
  // 上次选中的券快筛对应的韦恩ID，用于判断取消选中券快筛的场景
  const lastSelectWayneIdRef = useRef<number | null>(null);
  // 记录是否已经首次初始化过，用于区分首次复用SSR数据和后续刷新
  const hasCSRInitializedRef = useRef(false);
  // 跳过第一次 useEffect 调用
  const isSkipInitRef = useRef(false);

  // 请求 Tabbar 数据
  const getTabListData = useCallback(async () => {
    // 首次初始化：如果 SSR 已经预取了数据，并且 SSR 请求的手选 cityId || 实际 cityId 和 CSR 的相同，就跳过首次请求
    if (!hasCSRInitializedRef.current) {
      if (tabbarData && tabbarData.tabList?.length > 0) {
        setModuleLoaded('tabbar');
        if (locationInfo?.ssrDiff?.isSameSelectCityId) {
          hasCSRInitializedRef.current = true;
          return;
        }
      }
    }
    const getTabDataStartTime = Date.now();
    // 上报请求金刚区数据开始时间
    if (!isInitFinished && (window as any)?.__PAGE_JS_START__) {
      reportPerfMetric({
        key: "getTabDataStart",
        value: getTabDataStartTime - (window as any)?.__PAGE_JS_START__,
      })
    }
    try {
      const commonParams = await getCommonParams();
      const params: TabListRequestParams = {
        ...commonParams,
        tabId: null,
        wayneId: null,
        searchRuleScene,
        segmentIdList: null,
        cType: C_TYPE_ENUM.MT_MINI || "",
      };
      const { data } = await getTabList(params);
      const { tabList = [] } = data || {};
      const activeTab = tabList[0];
      setTabList(tabList);
      setActiveTab(activeTab);
      // 上报获取导航栏数据成功率
      reportGetTabListSuccessRate(tabList);
      if (!isInitFinished && tabList.length && searchRuleScene === 19) {
        // 上报导航栏接口响应次数
        reportCustomMetric({
          key: "TabbarResCount",
          value: 1,
          tags: {
            tabName: tabList[0]?.name,
            isSupportAPI: typeof window !== "undefined" && "IntersectionObserver" in window,
          }
        })
      }
    } catch (error) {
      setTabList([]);
      setCouponSelectionList([]);
      setQuickSelectionList([]);
      setActiveTab(null);
    } finally {
      hasCSRInitializedRef.current = true;
      if (!isInitFinished) {
        const getTabDataEndTime = Date.now();
        setIsInitFinished(true);
        setModuleLoaded('tabbar');
        // 上报请求金刚区数据耗时
        reportPerfMetric({
          key: "getTabDataCost",
          value: getTabDataEndTime - getTabDataStartTime,
        })
        // 上报请求金刚区数据结束时间
        if ((window as any)?.__PAGE_JS_START__) {
          reportPerfMetric({
            key: "getTabDataEnd",
            value: getTabDataEndTime - (window as any)?.__PAGE_JS_START__,
          })
        }
      }
    }
  }, [locationInfo, searchRuleScene, setModuleLoaded]);

  /**
   * 获取普通快筛列表
   * 调用场景：
   * 1. 用户切换 Tab，如果接口开启了懒加载，需要根据 tabId 重新获取普通快筛列表
   * 2. 用户选中券快筛时，根据 wayneId 获取普通快筛列表
   */
  const getQuickSelection = useCallback(
    async (activeTab?: Tab | null, activeCouponSelection?: CouponSelection | null) => {
      try {
        // 有韦恩ID，尝试从缓存获取普通快筛列表，券快筛对应的普通快筛列表只和 wayneId 有关，和 tabId 无关
        const wayneId = activeCouponSelection?.value ?? null;
        const activeTabId = activeTab?.id ?? null;
        if (wayneId) {
          const cacheResult = getCacheQuickSelectionList(wayneId);
          if (cacheResult.isCacheValid) {
            // 缓存可用，更新普通快筛列表
            setQuickSelectionList(cacheResult.quickSelectionList);
            return;
          }
        }

        // 无可用缓存，或者无韦恩ID，请求接口获取普通快筛列表
        const commonParams = await getCommonParams();
        const segmentIdList = activeCouponSelection?.coupon?.couponSupply?.segmentIdList
          || activeTab?.segmentIdList || null;
        const params: TabListRequestParams = {
          ...commonParams,
          tabId: activeTabId,
          wayneId,
          searchRuleScene,
          segmentIdList,
          cType: C_TYPE_ENUM.MT_MINI,
        };
        const { data } = await getTabList(params);
        const { tabList = [] } = data || {};
        const tab: Tab | undefined = tabList.find((tab) => tab.id === activeTabId);
        const quickSelectionList = tab?.quickSelection || [];
        setQuickSelectionList(quickSelectionList);
        if (wayneId) {
          setCacheQuickSelectionList(wayneId, quickSelectionList);
        }
      } catch (error) {
        reportError({
          name: "[useTabbar] getWayneQuickSelectionError",
          msg: error,
          category: "jsError",
          level: Level.ERROR,
        });
        setQuickSelectionList([]);
      }
    },
    [searchRuleScene]
  );

  // 获取缓存的普通快筛列表
  const getCacheQuickSelectionList = useCallback((wayneId: number) => {
    if (!wayneId) {
      return NO_QUICK_SELECTION_LIST_CACHE;
    }
    const cacheData = cacheQuickSelectionListRef.current.get(wayneId);
    const isCacheExpired = cacheData && Date.now() - cacheData.timestamp > 10 * 1000;

    // 如果缓存数据不存在或过期，返回空数据
    if (!cacheData || isCacheExpired) {
      return NO_QUICK_SELECTION_LIST_CACHE;
    } else {
      // 否则返回缓存数据
      return {
        isCacheValid: true,
        quickSelectionList: cacheData.quickSelectionList,
      };
    }
  }, []);

  // 设置缓存的普通快筛列表
  const setCacheQuickSelectionList = useCallback(
    (wayneId: number, quickSelectionList: QuickSelection[]) => {
      if (!wayneId || !quickSelectionList) {
        return;
      }
      cacheQuickSelectionListRef.current.set(wayneId, {
        quickSelectionList,
        timestamp: Date.now(),
      });
    },
    []
  );

  /** --- 用户交互 start --- */
  // 切换 Tab
  const handleTabClick = useCallback((tab: Tab) => {
    setActiveTab(tab);
    // tabId 不为空，说明点击的不是第一个 Tab，如果用户之前点击了红包墙的去使用，此时需要清空记录
    if (tab.id) {
      setUseCouponSelection(null);
    }
  }, []);

  // 选中普通快筛
  const handleQuickSelectionClick = useCallback(
    (selection: QuickSelection) => {
      setActiveQuickSelection(selection.id === activeQuickSelection?.id ? null : selection);
    },
    [activeQuickSelection]
  );

  // 点击券快筛
  const handleCouponSelectionClick = useCallback(
    (selection: CouponSelection) => {
      // 更新选中的券快筛，如果和已选中的相同，则取消选中
      setActiveCouponSelection(selection.id === activeCouponSelection?.id ? null : selection);
      // 取消选中普通快筛
      setActiveQuickSelection(null);
      // 判断点击的券快筛为去使用的券，更新全局状态中的 useCouponSelection
      if (selection.id === useCouponSelection?.id) {
        setCouponRecallInfo(null);
        setUseCouponSelection(null);
      }
    },
    [activeCouponSelection, useCouponSelection]
  );

  // 用户点击了红包墙的去使用：更新券快筛
  const handleCouponGoToUseClick = useCallback(
    (couponRecallInfo: CouponData) => {
      if (couponRecallInfo?.couponSupply?.wayneActId) {
        // 选中了券快筛
        const couponSelection = formatCouponSelection(couponRecallInfo);
        if (couponSelection?.id !== useCouponSelection?.id) {
          // 找到第一个 Tab（只有第一个 Tab 有券快筛）切换过去
          const waimaiTab = tabList.find((tab) => tab.listScene === defaultBizScene) || null;
          setActiveTab(waimaiTab);
          setUseCouponSelection(couponSelection);
        }
      }
    },
    [tabList, defaultBizScene]
  );
  /** --- 用户交互 end --- */

  // 切换 Tab：更新普通快筛列表，取消选中普通快筛和券快筛。
  // 如果选中了第一个 Tab，需要判断是否去使用场景，选中券快筛
  useEffect(() => {
    setQuickSelectionList(activeTab?.quickSelection || []);
    setActiveQuickSelection(null);
    setActiveCouponSelection(activeTab?.id ? null : useCouponSelection);
  }, [activeTab, tabList, useCouponSelection]);

  // 切换 Tab 或券快筛：需要重置普通快筛选项，选中券快筛的场景优先获取缓存，没有缓存则重新请求接口获取
  // 1. 用户主动切换 Tab：调用 handleTabClick，更新 activeTab，activeCouponSelection 不变
  // 2. 用户主动切换券快筛：调用 handleCouponSelectionClick，更新 activeCouponSelection，activeTab 不变
  // 3. 用户主动切换普通快筛：调用 handleQuickSelectionClick，activeTab，activeCouponSelection 不变
  // 4. 用户点击红包墙去使用：调用 handleCouponGoToUseClick，更新 activeTab、useCouponSelection，引发更新 activeCouponSelection
  useEffect(() => {
    // 跳过初始化调用
    if (!isSkipInitRef.current) {
      isSkipInitRef.current = true;
      return;
    }

    const wayneId = activeCouponSelection?.value ?? null;
    if (wayneId) {
      // 1. 有韦恩 Id，说明是以下场景：用户主动选中券快筛、用户点击红包墙去使用按钮自动选中了券快筛
      // 此时展示券快筛下的普通快筛，检查是否有缓存可用
      lastSelectWayneIdRef.current = wayneId;
      setActiveQuickSelection(null);
      setQuickSelectionList([]);
      getQuickSelection(activeTab, activeCouponSelection);
    } else if (lastSelectWayneIdRef.current || enableQuickSelectionLazyLoad) {
      // 2. 没有韦恩 Id，只在以下场景刷新普通快筛：
      //    2.1 取消选中券快筛
      //    2.2 设置了 enableQuickSelectionLazyLoad，用户切换了 Tab
      lastSelectWayneIdRef.current = null;
      setActiveQuickSelection(null);
      setQuickSelectionList([]);
      getQuickSelection(activeTab);
    }
  }, [activeTab?.id ?? null, activeCouponSelection?.value ?? null, getQuickSelection]);

  // 红包墙接口返回了可召回供给的券数据：格式化为券快筛列表
  useEffect(() => {
    if (!Array.isArray(quickScreenCouponList)) return;

    const couponSelectionList = quickScreenCouponList
      .filter((item) => item?.couponSupply?.wayneActId)
      .map(formatCouponSelection)
      .slice(0, 2);
    setCouponSelectionList(couponSelectionList);
  }, [quickScreenCouponList]);

  return {
    isInitFinished,
    tabList,
    activeTab,
    useCouponSelection,
    quickSelectionList,
    couponSelectionList,
    activeQuickSelection,
    activeCouponSelection,
    getTabListData,
    handleTabClick,
    handleQuickSelectionClick,
    handleCouponSelectionClick,
    handleCouponGoToUseClick,
  };
}
